import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';

import cn from 'classnames';
import { useRouter } from 'next/router';
import { IconButton } from 'components';
import SelectionTickMark from 'components/form/SelectionTickMark';
import { MatchesEmailFrequency } from 'hooks/useCandidateMatches/useCandidateMatchesInternal';
import { useCandidate } from 'hooks/useCandidate';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from './MatchesFrequency.module.scss';

interface MatchesFrequencyProps {
  /** Custom class name */
  className?: string;
  isModal?: boolean;
  onFrequencyChange?: (frequency: string) => void;
  closeModal?: () => void;
}

export const MatchesFrequency: React.FC<MatchesFrequencyProps> = memo(
  ({
    className = '',
    isModal = false,
    onFrequencyChange,
    closeModal,
  }: MatchesFrequencyProps) => {
    const [t] = useTranslation('candidate-dashboard');

    const {
      updateCandidateEmailMatchesFrequency,
      updateShowEmailMatchesFrequency,
    } = useCandidate();
    const { candidate } = useAuth();
    const router = useRouter();

    const [selectedFrequency, setSelectedFrequency] = useState(
      candidate?.matches_email_frequency
    );

    /** Handling back button action on browser */

    useEffect(() => {
      router.beforePopState(() => {
        if (isModal && closeModal) {
          closeModal();
        }
        return true;
      });

      return () => {
        router.beforePopState(() => true);
      };
    }, [router]);

    useEffect(() => {
      setSelectedFrequency(candidate?.matches_email_frequency);
    }, [candidate]);

    const handleCancel = () => {
      closeModal && closeModal();
    };
    const updateShowEmailMatchesFrequencyfunc = async () => {
      await updateShowEmailMatchesFrequency({ show: false });
    };

    const onChangeFrequency = async (frequency: MatchesEmailFrequency) => {
      setSelectedFrequency(frequency);
      if (!isModal) {
        await updateCandidateEmailMatchesFrequency({
          matchesFrequency: frequency,
        });
      }

      onFrequencyChange && onFrequencyChange(frequency);
    };

    return (
      <div
        className={cn(styles.container, className, {
          [styles.containerDashboard]: !isModal,
        })}
      >
        {isModal && (
          <div className={styles.headerWrapperModal}>
            <p className={styles.titleModal}>{t('matches-frequency.title')}</p>
            <IconButton
              iconName="icon_close"
              className={cn(styles.icon)}
              size="auto"
              onClick={
                isModal ? handleCancel : updateShowEmailMatchesFrequencyfunc
              }
            />
          </div>
        )}
        <div
          className={cn(styles.wrapper, {
            [styles.wrapper1]: !isModal,
            [styles.wrapper2]: isModal,
          })}
        >
          {!isModal && (
            <div className={styles.headerWrapper}>
              <p className={cn(styles.title, { [styles.titleModal]: isModal })}>
                {t('matches-frequency.title')}
              </p>
              <IconButton
                iconName="icon_close"
                className={cn(styles.icon, { [styles.iconModal]: isModal })}
                size="auto"
                onClick={
                  isModal ? handleCancel : updateShowEmailMatchesFrequencyfunc
                }
              />
            </div>
          )}
          <SelectionTickMark
            checked={selectedFrequency === MatchesEmailFrequency.DAILY}
            isTickSquare={true}
            onChange={() => onChangeFrequency(MatchesEmailFrequency.DAILY)}
            className={cn(styles.selectionbox, {
              [styles.selectionboxModal]: isModal,
              [styles.selectionboxChecked]:
                selectedFrequency === MatchesEmailFrequency.DAILY,
            })}
            unTickClassName={styles.untick}
            tickClassName={styles.tick}
          >
            <span className={styles.displayname}>
              {t('matches-frequency.daily.title')}
            </span>

            <p className={styles.labelDesc}>
              <ul>
                <li>{t('matches-frequency.daily.desc1')}</li>
                <li>{t('matches-frequency.daily.desc2')}</li>
                <li>{t('matches-frequency.daily.desc3')}</li>
              </ul>
            </p>
          </SelectionTickMark>
          <SelectionTickMark
            checked={selectedFrequency === MatchesEmailFrequency.WEEKLY}
            isTickSquare={true}
            onChange={() => onChangeFrequency(MatchesEmailFrequency.WEEKLY)}
            className={cn(styles.selectionbox, {
              [styles.selectionboxModal]: isModal,
              [styles.selectionboxChecked]:
                selectedFrequency === MatchesEmailFrequency.WEEKLY,
            })}
            unTickClassName={styles.untick}
            tickClassName={styles.tick}
          >
            <span className={styles.displayname}>
              {t('matches-frequency.weekly.title')}
            </span>

            <p className={styles.labelDesc}>
              <ul>
                <li>{t('matches-frequency.weekly.desc')}</li>
              </ul>
            </p>
          </SelectionTickMark>
          <SelectionTickMark
            checked={selectedFrequency === MatchesEmailFrequency.NEVER}
            isTickSquare={true}
            onChange={() => onChangeFrequency(MatchesEmailFrequency.NEVER)}
            className={cn(styles.selectionbox, {
              [styles.selectionboxModal]: isModal,
              [styles.selectionboxChecked]:
                selectedFrequency === MatchesEmailFrequency.NEVER,
            })}
            unTickClassName={styles.untick}
            tickClassName={styles.tick}
          >
            <span className={styles.displayname}>
              {t('matches-frequency.never.title')}
            </span>

            <p className={styles.labelDesc}>
              <ul>
                <li>{t('matches-frequency.never.desc')}</li>
              </ul>
            </p>
          </SelectionTickMark>
        </div>
      </div>
    );
  }
);
MatchesFrequency.displayName = 'MatchesFrequency';
